import type { ITrackedTime } from '../models/IEmployeeTrackedTime';
import type { ITopData } from '../models/ITopData';
import type {
  ISummaryCurrencyTrackedTime,
  ISummaryDualCurrencyResponse,
} from '../models/ISummaryDualCurrency';
import { formatHours } from '../services/commonFunctions';

export const buildTotalsBreakdown = (block?: ISummaryCurrencyTrackedTime): ITopData[] => [
  {
    Id: 0,
    Name: 'Tracking App',
    TotalHours: 0,
    TotalPrice: block?.ViaTrackingApp ?? 0,
  },
  {
    Id: 0,
    Name: 'Manually',
    TotalHours: 0,
    TotalPrice: block?.Manually ?? 0,
  },
];

export const formatPlainAmount = (value: number, fractionDigits = 2): string => {
  const safe = Number.isFinite(value) ? value : 0;
  return safe.toFixed(fractionDigits);
};

export const pickTrackedTimeBucket = (
  response: ISummaryDualCurrencyResponse<ITrackedTime>,
  currencyCode?: string
): ITrackedTime[] => (currencyCode === 'EUR' ? response.Eur : response.Usd);

export const parseTrackedTimeDates = (items: ITrackedTime[]): ITrackedTime[] =>
  items.map((item) => ({ ...item, Date: new Date(item.Date) }));

export const formatTrackedTimePreviewLine = (
  price: number | null | undefined,
  hours: number | null | undefined,
  currencySymbol: string
): string => {
  const safePrice = Number.isFinite(price ?? NaN) ? (price as number) : 0;
  const safeHours = Number.isFinite(hours ?? NaN) ? (hours as number) : 0;
  return `${currencySymbol}${safePrice.toFixed(2)} (${formatHours(safeHours)}h)`;
};

export const findWeekTrackedTime = (
  data: ITrackedTime[] | undefined,
  sundayDate: Date
): ITrackedTime | undefined => {
  if (!data) {
    return undefined;
  }

  const weekStartDate = new Date(
    sundayDate.getFullYear(),
    sundayDate.getMonth(),
    sundayDate.getDate() - 6
  );

  return data.find((item) => item.Date.toDateString() === weekStartDate.toDateString());
};

export const findMonthTrackedTime = (
  data: ITrackedTime[] | undefined,
  monthDate: Date
): ITrackedTime | undefined =>
  data?.find(
    (item) =>
      item.Date.getFullYear() === monthDate.getFullYear() &&
      item.Date.getMonth() === monthDate.getMonth()
  );

export const formatSummaryFooterText = (
  totalInEur: number,
  rate: number | null | undefined
): string => {
  const amount = formatPlainAmount(totalInEur);
  if (rate == null || rate === 0) {
    return `Total in EUR ${amount}`;
  }

  return `Total in EUR ${amount} (used EUR/USD rate ${rate})`;
};
